import { Suspense, lazy, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import LoadingReticle from './components/LoadingReticle';
import RouteFallback from './components/RouteFallback';
import VelocitySeasoning from './components/presentation/VelocitySeasoning';
import { SmoothScroll } from './lib/smoothScroll';

// Task 4.2 — route-level code splitting (DECISIONS.md D-39). Every page component
// is lazy-loaded so a visitor only downloads the JS for the route they land on,
// not the entire site's page markup up front. The shared animation stack
// (gsap/ScrollTrigger/Lenis via SmoothScroll, framer-motion via the shared nav/
// footer/motion helpers) still lands in the common chunk because App.js and the
// shared chrome import it eagerly — but each page's own body, and homepage-only
// heavy pieces like GSAP's Flip plugin, now split into per-route chunks. The
// <Suspense> boundary below renders RouteFallback for the brief chunk-download gap.
const LandingPage = lazy(() => import('./LandingPage'));
const PricingPage = lazy(() => import('./PricingPage'));
const SolutionsPage = lazy(() => import('./SolutionsPage'));
const CaseStudiesPage = lazy(() => import('./CaseStudiesPage'));
const ContactPage = lazy(() => import('./ContactPage'));
const BookDemoPage = lazy(() => import('./BookDemoPage'));
const BlogListPage = lazy(() => import('./BlogListPage'));
const BlogPostPage = lazy(() => import('./BlogPostPage'));
const PrivacyPolicy = lazy(() => import('./PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./TermsOfUse'));

// Task 3.5 — native View Transitions for route changes (DECISIONS.md D-19).
// On each route change we wrap the React commit in document.startViewTransition()
// so the outgoing and incoming pages cross-fade (the browser's default
// view-transition animation) instead of hard-cutting. This removes the abrupt
// route swap without adding a dependency — startViewTransition() is a native
// browser API. It is feature-detected: browsers without support fall back to an
// instant swap, exactly the behavior we have today. The reduced-motion guard in
// index.css also collapses the cross-fade to instant for users who opt out.
//
// Implementation: <Routes> renders against a `displayLocation` that lags the real
// location until the transition callback runs, so the browser can capture the
// "before" frame, then flushSync commits the new page synchronously inside the
// transition for the "after" frame.
function AnimatedRoutes() {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);

  useEffect(() => {
    if (location.key === displayLocation.key) return;
    if (typeof document === 'undefined' || !document.startViewTransition) {
      setDisplayLocation(location);
      return;
    }
    document.startViewTransition(() => {
      flushSync(() => setDisplayLocation(location));
    });
  }, [location, displayLocation]);

  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes location={displayLocation}>
      <Route path="/" element={<LandingPage />} />
      <Route path="/solutions" element={<SolutionsPage />} />
      <Route path="/case-studies" element={<CaseStudiesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/book-demo" element={<BookDemoPage />} />
      <Route path="/contact" element={<ContactPage />} />
      {/* Blog / Resources (Task 5.1 / D-40) — list + post detail, same
          React.lazy route-splitting convention as every other page. An unknown
          /blog/:slug redirects to /blog inside BlogPostPage (not a 404). */}
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      {/* IA consolidation (Task 3.6 / D-21 and Task 3.12 / D-35): lower-traffic
          pages are folded into pages that already exist as anchor sections.
          /industries + /how-it-works fold into /solutions; /faq folds into
          /solutions#faq; /about folds into /contact#about. Old routes redirect
          (not 404) to preserve inbound links/bookmarks/SEO. */}
      <Route path="/industries" element={<Navigate to="/solutions#industries" replace />} />
      <Route path="/how-it-works" element={<Navigate to="/solutions#how-it-works" replace />} />
      <Route path="/faq" element={<Navigate to="/solutions#faq" replace />} />
      <Route path="/about" element={<Navigate to="/contact#about" replace />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfUse />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <Router>
      {/* Inertial smooth scroll site-wide via Lenis, synced to GSAP's ticker /
          ScrollTrigger (Task 3.9 Round 3 / D-29). No-op under reduced motion. */}
      <SmoothScroll />
      {/* Scroll-velocity "seasoning" (Task 3.11 Effect 6 / D-34): maps Lenis's
          velocity to a clamped ±3° --scroll-skew CSS var read by .vel-season
          elements; site-wide but subtle, flat at rest, no-op under reduced motion. */}
      <VelocitySeasoning />
      {/* One-time reticle "lock-on" on cold load (Task 3.9); skipped under
          reduced motion and self-unmounts after ~650ms. */}
      <LoadingReticle />
      <AnimatedRoutes />
    </Router>
  );
}
